/**
 * AdminQrLabels — print bulk QR label sheets for Products or Assets.
 *
 * Flow:
 *   1. Pick entity type (Product / Asset).
 *   2. Search / multi-select rows (left rail).
 *   3. Pick a label layout (Avery / Brother / Dymo).
 *   4. Click "Generate PDF" → downloads a print-ready PDF.
 *
 * Route: /admin/qr-labels
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Printer, Tag, Box, ShoppingCart, Search, Download, RefreshCw,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------------------------------------------------------------------------
// Data sources — we use lightweight read endpoints already in the API.
// (POS products + assets each expose a /list endpoint.)
// ---------------------------------------------------------------------------
const useEntities = (entity) => useQuery({
  queryKey: ['qr-labels-entities', entity],
  queryFn: async () => {
    const url = entity === 'product'
      ? `${API}/api/ops/pos/products`
      : `${API}/api/facilities/assets`;
    const { data } = await axios.get(url, { headers: authH() });
    const rows = Array.isArray(data) ? data : (data.rows || data.products || data.items || []);
    return rows.map(r => ({
      id: r.id || r._id,
      name: r.name || r.title || '(unnamed)',
      code: r.sku || r.asset_tag || r.serial_number || '',
      category: r.category || r.type || '',
    }));
  },
});

const AdminQrLabels = () => {
  const [entity, setEntity] = useState('product');
  const [layout, setLayout] = useState('avery_5160');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [copies, setCopies] = useState(1);
  const [includeName, setIncludeName] = useState(true);
  const [includeCode, setIncludeCode] = useState(true);
  const [busy, setBusy] = useState(false);

  const { data: entities = [], isLoading } = useEntities(entity);

  const { data: layouts = [] } = useQuery({
    queryKey: ['qr-labels-layouts'],
    queryFn: async () => (
      await axios.get(`${API}/api/qr-labels/layouts`, { headers: authH() })
    ).data,
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return entities;
    return entities.filter(e =>
      (e.name && e.name.toLowerCase().includes(q)) ||
      (e.code && e.code.toLowerCase().includes(q)) ||
      (e.category && e.category.toLowerCase().includes(q))
    );
  }, [entities, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(filtered.map(f => f.id)));
  const clearAll = () => setSelected(new Set());

  const totalLabels = selected.size * copies;
  const layoutMeta = layouts.find(l => l.key === layout);

  const onEntityChange = (e) => {
    setEntity(e);
    setSelected(new Set());
  };

  const generate = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one item');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/qr-labels/render`,
        {
          entity,
          ids: [...selected],
          layout,
          copies_per_item: copies,
          include_name: includeName,
          include_code: includeCode,
        },
        { headers: authH(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-labels-${entity}-${layout}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`PDF generated — ${totalLabels} labels`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'PDF generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        <header>
          <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
            <Printer className="h-6 w-6 text-indigo-400" /> QR Label Printer
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Pick products or assets, choose a label layout, and we&apos;ll generate a print-ready PDF sheet.
          </p>
        </header>

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Left rail — entity picker */}
          <Card className="lg:col-span-7 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  {entity === 'product' ? <ShoppingCart className="h-4 w-4" /> : <Box className="h-4 w-4" />}
                  Select {entity === 'product' ? 'products' : 'assets'}
                </span>
                <div className="flex items-center gap-2">
                  <Select value={entity} onValueChange={onEntityChange}>
                    <SelectTrigger className="w-full max-w-[140px] bg-slate-900 border-slate-800 h-8"
                                    data-testid="entity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Products</SelectItem>
                      <SelectItem value="asset">Assets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input value={search} onChange={e => setSearch(e.target.value)}
                         placeholder="Search by name, code, or category…"
                         className="pl-9 bg-slate-900 border-slate-800"
                         data-testid="qr-labels-search" />
                </div>
                <Button variant="outline" size="sm" onClick={selectAll}
                         data-testid="qr-labels-select-all">Select all</Button>
                <Button variant="ghost" size="sm" onClick={clearAll}>Clear</Button>
              </div>
              <div className="max-h-[480px] overflow-y-auto space-y-1"
                   data-testid="qr-labels-list">
                {isLoading && <p className="text-xs text-slate-500">Loading…</p>}
                {!isLoading && filtered.length === 0 && (
                  <p className="text-xs text-slate-500">No matching {entity}s found.</p>
                )}
                {filtered.map(e => {
                  const on = selected.has(e.id);
                  return (
                    <label key={e.id}
                           className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                             on ? 'bg-indigo-500/15 border-indigo-400/40' : 'bg-slate-800/30 border-slate-800 hover:border-slate-700'
                           }`}
                           data-testid={`qr-labels-row-${e.id}`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(e.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{e.name}</div>
                        <div className="text-[10px] text-slate-500">{e.code} {e.category && `· ${e.category}`}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Right rail — layout + generate */}
          <Card className="lg:col-span-5 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
                <Tag className="h-4 w-4" /> Layout & options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Label layout</label>
                <Select value={layout} onValueChange={setLayout}>
                  <SelectTrigger className="bg-slate-900 border-slate-800"
                                  data-testid="layout-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {layouts.map(l => (
                      <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Copies per item</label>
                  <Input type="number" min={1} max={100} value={copies}
                         onChange={e => setCopies(Math.max(1, Math.min(100, +e.target.value || 1)))}
                         className="bg-slate-900 border-slate-800"
                         data-testid="qr-labels-copies" />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-slate-400 pt-1">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeName}
                          onChange={e => setIncludeName(e.target.checked)} />
                  Show name
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={includeCode}
                          onChange={e => setIncludeCode(e.target.checked)} />
                  Show code
                </label>
              </div>

              <div className="border border-slate-800 rounded-md p-3 bg-slate-800/30 text-xs text-slate-400 space-y-1">
                <div className="flex justify-between">
                  <span>Selected items</span>
                  <Badge className="bg-indigo-500/20 border-indigo-400/40 text-indigo-200 border" data-testid="qr-labels-selected-count">
                    {selected.size}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Total labels</span>
                  <span className="text-slate-200 font-medium tabular-nums">{totalLabels}</span>
                </div>
                {layoutMeta && (
                  <div className="pt-2 border-t border-slate-700/50 text-slate-500">
                    {layoutMeta.label}
                  </div>
                )}
              </div>

              <Button onClick={generate} disabled={selected.size === 0 || busy}
                      className="w-full" data-testid="qr-labels-generate-btn">
                {busy ? (
                  <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Generating…</>
                ) : (
                  <><Download className="h-4 w-4 mr-1" /> Generate PDF</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminQrLabels;
